import React, { useEffect, useState } from 'react';
import { FileText, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Banner de atualização de Política/Termos — Fase 3 LGPD.
 *
 * Comportamento:
 * - Aparece APENAS para usuários autenticados (não aparece na tela de login)
 * - É dismissível (botão X) — fica oculto após o usuário fechar (persiste no localStorage)
 * - Tem expiração automática 30 dias após a publicação (12/05/2026)
 * - Persistência via localStorage com chave versionada — quando publicar
 *   nova atualização, basta bumpar BANNER_VERSION e o banner reaparece
 *
 * Como atualizar para uma nova versão:
 *   1. Incrementa BANNER_VERSION abaixo (ex: v2.0 → v2.1)
 *   2. Atualiza PUBLISHED_AT para a nova data
 *   3. Ajusta o texto se necessário
 */

const BANNER_VERSION = 'v2.0';
const PUBLISHED_AT = new Date('2026-05-12T00:00:00');
const DISPLAY_DURATION_DAYS = 30;
const STORAGE_KEY = `iat_policy_banner_${BANNER_VERSION}_dismissed`;

const PolicyUpdateBanner = () => {
  const auth = useAuth();
  const isAuthenticated = !!auth?.user;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Só aparece para usuários autenticados (não na tela de login)
    if (!isAuthenticated) {
      setVisible(false);
      return;
    }

    try {
      // Foi fechado pelo usuário?
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (dismissed === 'true') {
        return;
      }

      // Já passou do prazo de 30 dias desde a publicação?
      const now = new Date();
      const expiresAt = new Date(PUBLISHED_AT);
      expiresAt.setDate(expiresAt.getDate() + DISPLAY_DURATION_DAYS);
      if (now > expiresAt) {
        return;
      }

      setVisible(true);
    } catch (e) {
      // localStorage pode estar indisponível (modo anônimo strict) — não mostra
    }
  }, [isAuthenticated]);

  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch (e) {
      // Falha silenciosa
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="banner"
      aria-label="Aviso de atualização de Política e Termos"
      style={{
        // Toast flutuante — não empurra o layout (não interfere com chat/composer)
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9999,
        maxWidth: 420,
        width: 'calc(100% - 32px)',
        background: '#232329', // charcoal (card IAT)
        border: '1px solid #3A3A44', // steel
        borderLeft: '3px solid #3B82F6', // Info IAT (semântica)
        color: '#FAFAFB', // paper
        padding: '14px 16px',
        borderRadius: 12,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        fontSize: 13,
        lineHeight: 1.5,
        boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
        fontFamily: 'inherit',
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: 'rgba(59,130,246,0.14)',
          border: '1px solid rgba(59,130,246,0.28)',
          color: '#3B82F6',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 1,
        }}
        aria-hidden="true"
      >
        <FileText size={14} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "'Inter Tight', 'Inter', system-ui, sans-serif",
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: '-0.01em',
            color: '#FAFAFB',
            marginBottom: 4,
          }}
        >
          Atualização de Termos e Política
        </div>
        <div style={{ color: '#9A9AA5' /* silver */ }}>
          Atualizamos nossos{' '}
          <a
            href="https://www.iatconsulting.com.br/termos-condicoes"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#FFC107', textDecoration: 'none', fontWeight: 600 }}
          >
            Termos
          </a>{' '}
          e{' '}
          <a
            href="https://www.iatconsulting.com.br/politica-privacidade"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#FFC107', textDecoration: 'none', fontWeight: 600 }}
          >
            Política de Privacidade
          </a>
          . Ao continuar usando a plataforma você confirma ciência das alterações.
        </div>
      </div>

      <button
        onClick={handleDismiss}
        aria-label="Fechar aviso"
        className="btn-icon"
        style={{
          background: 'transparent',
          border: 'none',
          color: '#9A9AA5',
          width: 24,
          height: 24,
          cursor: 'pointer',
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          borderRadius: 6,
          transition: 'color 0.15s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#FAFAFB'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = '#9A9AA5'; }}
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default PolicyUpdateBanner;
